import { z } from 'zod'

/**
 * Regla de fortaleza de contraseña (única fuente de verdad).
 * Mínimo 8 caracteres con al menos una mayúscula y un número.
 *
 * Esta es la regla que el formulario de registro y la pantalla de cambio de
 * contraseña aplican. Antes había una regla más laxa hardcodeada en
 * pages/auth/index.vue ("min 6"), creando inconsistencia con el schema.
 */
export const passwordSchema = z
  .string()
  .min(8, 'Mínimo 8 caracteres')
  .regex(/[A-Z]/, 'Debe incluir al menos una mayúscula')
  .regex(/[0-9]/, 'Debe incluir al menos un número')

/** Helper imperativo para usar fuera de un schema completo (ej. reset-password). */
export function validatePasswordStrength(value: string): { valid: boolean; message?: string } {
  const r = passwordSchema.safeParse(value)
  if (r.success) return { valid: true }
  return { valid: false, message: r.error.issues[0]?.message || 'Contraseña no válida' }
}

export const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'El email es obligatorio')
    .email('Introduce un email válido'),
  password: z
    .string()
    .min(1, 'La contraseña es obligatoria'),
})

export const registerSchema = z.object({
  email: z
    .string()
    .min(1, 'El email es obligatorio')
    .email('Introduce un email válido'),
  password: passwordSchema,
  nombre_completo: z
    .string()
    .min(2, 'El nombre debe tener al menos 2 caracteres')
    .max(100, 'Máximo 100 caracteres'),
  confirmPassword: z
    .string()
    .min(1, 'Repite la contraseña'),
  acceptTerms: z
    .literal(true, { message: 'Debes aceptar los términos y condiciones' }),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Las contraseñas no coinciden',
  path: ['confirmPassword'],
})

export type LoginInput = z.infer<typeof loginSchema>
export type RegisterInput = z.infer<typeof registerSchema>
